import { QueryKey } from '@/configs/queryKeys';
import { UUID } from 'crypto';
import { ProductType } from './ProductTypeApi';
import { DateValue } from '@mantine/dates';
import { Attachment } from '@/types';
import {
	FormAnswerMap,
	FormDetails,
	QuantityPerDimensionForm,
	ShippingData,
	UserData,
} from '@/ui/productTypeForm/ProductTypeForm';
import EntityApi, { BaseEntity } from './EntityApi';
import httpClient from './httpClient';
import { FormTemplate, QuestionType } from '@/remoteApi/types/form-template.types';

export enum FormStatusEnum {
	NEW_FORM = 'NEW_FORM',
	IN_VALUATION = 'IN_VALUATION',
	IN_PRODUCTION = 'IN_PRODUCTION',
	COMPLETED = 'COMPLETED',
}

export interface QuantityPerDimension {
	width: number;
	height: number;
	depth: number;
	quantity: number;
	price: number;
	isCustom: boolean;
}

export interface FormQuestion {
	questionId: UUID;
	name: string;
	questionGroup: string;
}

export interface FormAnswer {
	answerId: UUID | '';
	question: FormQuestion;
	answer: string;
	files: Attachment[];
}

export interface Form extends BaseEntity {
	externalOrderID?: UUID;
	productType: ProductType;
	orderId: string;
	userData: {
		name: string;
		surname: string;
		companyName: string;
		NIP: string;
		city: string;
		postalCode: string;
		address: string;
		email: string;
		phone: string;
	};
	shippingData: {
		city: string;
		postalCode: string;
		address: string;
	};
	status?: FormStatusEnum;
	quantity: number;
	orderDate: DateValue;
	note: string;
	price?: number;
	formAnswers: FormAnswer[];
	quantityPerDimension: QuantityPerDimension[];
}

class FormApi extends EntityApi<Form> {
	queryKey = QueryKey.forms;

	constructor() {
		super(QueryKey.forms);
		this.sendForm = this.sendForm.bind(this);
		this.getOne = this.getOne.bind(this);
	}

	async sendForm({
		productType,
		formTemplate,
		userData,
		shippingData,
		formDetails,
		answersMap,
		quantityPerDimension,
	}: {
		productType: ProductType;
		formTemplate: FormTemplate;
		userData: UserData;
		shippingData: ShippingData;
		formDetails: FormDetails;
		answersMap: Map<UUID, FormAnswerMap>;
		quantityPerDimension: QuantityPerDimensionForm;
	}) {
		const payload: Omit<Form, 'id' | 'createdAt' | 'updatedAt' | 'orderId'> = {
			productType,
			userData,
			shippingData,
			...formDetails,
			formAnswers: [],
			quantityPerDimension,
		};

		for (const questionGroup of formTemplate.questionGroups) {
			for (const question of questionGroup.questions) {
				const formAnswer = answersMap.get(question.id),
					asnwerTemplate = question.answers.find(
						(answer) => answer.id === formAnswer?.answerId
					);

				if (!formAnswer) continue;

				switch (question.questionType) {
					case QuestionType.DIMENSIONS:
						continue;
					case QuestionType.NUMBER:
					case QuestionType.TEXT:
					case QuestionType.TEXTAREA: {
						let answer = formAnswer.value;

						if (question.suffix) answer += ` ${question.suffix}`;

						payload.formAnswers.push({
							answerId: '',
							question: {
								questionId: question.id,
								name: question.name,
								questionGroup: questionGroup.name,
							},
							answer,
							files: [],
						});
						continue;
					}
					case QuestionType.FILES: {
						if (!formAnswer.attachments.length) continue;
						payload.formAnswers.push({
							answerId: '',
							question: {
								questionId: question.id,
								name: question.name,
								questionGroup: questionGroup.name,
							},
							answer: '',
							files: formAnswer.attachments,
						});
						continue;
					}
					default: {
						if (!asnwerTemplate) continue;
						const answer = question.suffix
							? `${asnwerTemplate.name} ${question.suffix}`
							: asnwerTemplate.name;

						payload.formAnswers.push({
							answerId: asnwerTemplate.id,
							question: {
								questionId: question.id,
								name: question.name,
								questionGroup: questionGroup.name,
							},
							answer,
							files: [],
						});
						continue;
					}
				}
			}
		}

		const response = await httpClient.post<Form>(this.queryKey, payload);
		return response.data;
	}
}

export default new FormApi();
